import {
  describePairing,
  describeShortfall,
  findRelationships,
  type Entry,
  type Track,
} from '@anytracker/core'

import { trackColour } from '../../charts/colours.js'
import styles from './Noticings.module.css'
import panel from './Relationships.module.css'

interface Props {
  readonly tracks: readonly Track[]
  readonly entries: readonly Entry[]
  readonly range: { readonly fromEpochMs: number; readonly toEpochMs: number }
  /** Called with the two track ids, so the overlay can be pointed at them. */
  readonly onCompare: (a: string, b: string) => void
}

const LIMIT = 4

/**
 * Which tracks move together (T-1203).
 *
 * The overlay can already draw any two tracks on one axis; what it cannot do is
 * tell you which two are worth drawing. This does, and then hands them straight
 * to it.
 *
 * **The denominator is on screen, always.** "3 of 84 combinations" is a very
 * different claim from "3 relationships found", and the second is how a
 * correlation search misleads people — the strongest of eighty-four looks strong
 * even in noise. Core counts what it examined; this states it.
 */
export function Relationships({ tracks, entries, range, onCompare }: Props) {
  const found = findRelationships(tracks, entries, range, { limit: LIMIT })
  const shortfall = describeShortfall(found)

  return (
    <section className={styles['section']} aria-labelledby="relationships-title">
      <h2 className={styles['title']} id="relationships-title">
        Tracks that move together
      </h2>

      {found.pairings.length === 0 ? (
        <p className={panel['nothing']}>{shortfall}</p>
      ) : (
        <>
          <ul className={styles['list']}>
            {found.pairings.map((pairing) => (
              <li
                key={`${pairing.a.id}-${pairing.b.id}-${String(pairing.lagDays)}`}
                className={styles['item']}
              >
                <span className={panel['pair']} aria-hidden="true">
                  <span
                    className={styles['mark']}
                    style={{ background: trackColour(pairing.a.id) }}
                  />
                  <span
                    className={styles['mark']}
                    style={{ background: trackColour(pairing.b.id) }}
                  />
                </span>
                <span className={styles['body']}>
                  <span className={styles['headline']}>
                    {pairing.a.name} · {pairing.b.name}
                  </span>
                  <span className={styles['detail']}>{describePairing(pairing)}</span>
                  <button
                    type="button"
                    className={panel['draw']}
                    onClick={() => onCompare(pairing.a.id, pairing.b.id)}
                  >
                    Draw them together
                  </button>
                </span>
              </li>
            ))}
          </ul>

          {/*
            The honesty line, and it is not optional.

            A ranked list with the denominator hidden is the single most
            misleading way to present a search: with eight tracks there are
            eighty-four combinations, and the top one is expected to look strong
            whether or not anything is there.
          */}
          <p className={panel['considered']}>
            {String(found.pairings.length)} of {String(found.considered)} combinations
            cleared the bar. Moving together is not the same as one causing the other.
          </p>
        </>
      )}
    </section>
  )
}
